import { Color, Spacing } from '@persona-id/design-tokens';
import { useCallback, useEffect } from 'react';
import styled from 'styled-components';

import GraphCanvasV2 from 'dashboard/components/GraphCanvasV2';
import GraphCanvasContextProvider from 'dashboard/components/GraphModule/GraphCanvasContextProvider';
import GraphConfigurationProvider from 'dashboard/components/GraphModule/GraphConfigurationProvider';
import GraphDataWrapper from 'dashboard/components/GraphModule/GraphDataWrapper';
import { useEventBus } from 'dashboard/components/GraphModule/hooks/useEventBus';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphConfiguration } from 'dashboard/components/GraphModule/hooks/useGraphConfiguration';
import { useGraphData } from 'dashboard/components/GraphModule/hooks/useGraphData';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { CollapsedGroupPreviewData, G6GraphUpdateMessage } from 'dashboard/models/graph';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

interface CollapsedGroupPreviewProps {
  height: number;
  width: number;
  data: CollapsedGroupPreviewData;
}

const GraphContainer = styled.div`
  background: ${Color.CoreSlate50};
  border-radius: ${Spacing[2]};
  position: relative;
  display: flex;
`;

const PreviewContainer = styled.div`
  position: sticky;
  overflow: hidden;
`;

const GraphCollapsedGroupPreview = ({ height, width, data }: CollapsedGroupPreviewProps) => {
  const eventBus = useEventBus();
  const g6Wrapper = useG6Wrapper();
  const { graphExplorer } = useGraphExplorer();
  const { areGraphConfigsFetched } = useGraphConfiguration();

  const handleG6Mount = () => {
    eventBus.initialize(g6Wrapper, window, graphExplorer, true);
    eventBus.setIsDisabled(true);
  };

  // -----------------------------------------------------------------------------------------
  // Canvas updates
  // -----------------------------------------------------------------------------------------
  const canvasUpdateCallback = useCallback(
    (message: G6GraphUpdateMessage) => {
      g6Wrapper.updateGraph(message);
    },
    [g6Wrapper],
  );
  useEffect(() => {
    graphExplorer.setCanvasUpdateCallback(canvasUpdateCallback);
  }, [graphExplorer, canvasUpdateCallback]);

  useEffect(() => {
    if (!areGraphConfigsFetched || !data || !graphExplorer) {
      return;
    }

    // load data into graph explorer and g6
    const lightHydrationDataMap = new Map();
    for (const [nodeId, lightHydrationData] of Object.entries(data.lightHydrationData)) {
      lightHydrationDataMap.set(nodeId, lightHydrationData);
    }
    const message = graphExplorer.setData(data.v2Data, lightHydrationDataMap, data.nodePositions);
    graphExplorer.setSeedNodes(data.v2Data.seedNodes.map((node) => node.id));

    g6Wrapper.setDataWithPositions(message);
  }, [data, graphExplorer, areGraphConfigsFetched, g6Wrapper]);

  return (
    <GraphContainer>
      <PreviewContainer>
        <GraphCanvasV2
          handleG6Mount={handleG6Mount}
          height={height}
          selectedNodes={[]}
          width={width}
        />
      </PreviewContainer>
    </GraphContainer>
  );
};

interface GraphCollapsedGroupPreviewWrapperProps {
  data: CollapsedGroupPreviewData;
  height: number;
  width: number;
}

const GraphCollapsedGroupPreviewWrapper = ({
  data,
  height,
  width,
}: GraphCollapsedGroupPreviewWrapperProps) => {
  const {
    accountStatuses,
    accountTypes,
    currentEnvironment,
    currentUser,
    graph,
    graphQueryTemplates,
  } = useGraphData();
  const viewTypeSwitchEnabled = useFeatureFlag('dashboard:graph-pairwise-similarity-nodes');

  return (
    <GraphDataWrapper
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountStatuses={accountStatuses}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountTypes={accountTypes}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentEnvironment={currentEnvironment}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentUser={currentUser}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graph={graph}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graphQueryTemplates={graphQueryTemplates}
    >
      <GraphCanvasContextProvider
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        graph={graph}
        viewTypeSwitchEnabled={viewTypeSwitchEnabled}
        viewType={data.viewType}
      >
        <GraphConfigurationProvider>
          <GraphCollapsedGroupPreview height={height} width={width} data={data} />
        </GraphConfigurationProvider>
      </GraphCanvasContextProvider>
    </GraphDataWrapper>
  );
};

export default GraphCollapsedGroupPreviewWrapper;
